/*
 * CUSTOM: Pickup availability (2026-10-09)
 * Refreshes the custom-pickup-availability block when the variant changes.
 * Based on local-pickup.js, but matches the fetched markup by block id so it
 * never picks up the Buy buttons block's own pickup row from the same section.
 */
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class CustomPickupAvailability extends HTMLElement {
  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {HTMLElement | null} */
  #drawer = null;

  /** @type {(() => void) | undefined} */
  #cleanup;

  connectedCallback() {
    // Portal the sibling drawer to document.body so its z-index isn't trapped
    // by ancestor stacking contexts.
    const drawer = this.#siblingDrawer(this);
    if (drawer) {
      this.#drawer = drawer;
      document.body.appendChild(drawer);
    }

    const closestSection = this.closest('.shopify-section, dialog');

    /** @param {any} event */
    const handleProductSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

      event.promise
        .then(({ detail }) => {
          if (!detail) return;

          const { newProduct, resource } = detail;
          if (newProduct) this.dataset.productUrl = newProduct.url;

          const variantId = resource ? String(resource.id) : null;
          if (variantId === this.dataset.variantId) return;

          if (variantId && resource.available) {
            this.dataset.variantId = variantId;
            this.#fetchAvailability(variantId);
          } else {
            this.setAttribute('hidden', '');
          }
        })
        .catch((/** @type {any} */ error) => {
          if (error?.name !== 'AbortError') console.warn('[custom-pickup] Event promise rejected:', error);
        });
    };

    closestSection?.addEventListener(StandardEvents.productSelect, handleProductSelect);

    this.#cleanup = () => {
      closestSection?.removeEventListener(StandardEvents.productSelect, handleProductSelect);
      this.#drawer?.remove();
      this.#drawer = null;
    };
  }

  disconnectedCallback() {
    this.#cleanup?.();
  }

  /** @param {Element} element */
  #siblingDrawer(element) {
    const next = element.nextElementSibling;
    return next instanceof HTMLElement && next.matches('theme-drawer.pickup-drawer') ? next : null;
  }

  /** @param {string} variantId */
  #fetchAvailability(variantId) {
    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    fetch(`${this.dataset.productUrl}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector(`custom-pickup-availability[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) {
          this.setAttribute('hidden', '');
          return;
        }

        morph(this, fresh);

        const newDrawer = this.#siblingDrawer(fresh);
        if (newDrawer && this.#drawer) {
          morph(this.#drawer, newDrawer);
        } else if (newDrawer) {
          this.#drawer = newDrawer;
          document.body.appendChild(newDrawer);
        }
      })
      .catch(() => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  }
}

if (!customElements.get('custom-pickup-availability')) {
  customElements.define('custom-pickup-availability', CustomPickupAvailability);
}
